import { Case, CasesService } from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

interface CaseContextProps {
  case: Case
  reload: (background?: boolean) => Promise<void>
}

const CaseContext = createContext<CaseContextProps>(undefined!)

export function useCase() {
  const context = useContext(CaseContext)
  if (context === undefined) {
    throw new Error('useCase must be used within a CaseContext.Provider route')
  }

  return context
}

export function CaseProvider({
  id,
  children,
  onError,
}: React.PropsWithChildren<{
  id: Case['id']
  onError?: (error: unknown) => void
}>) {
  // State
  const [loading, setLoading] = useState(true)
  const [_case, setCase] = useState<Case | null>(null)

  // Load the case details
  const reload = useCallback(
    async (background?: boolean) => {
      if (!id) return
      if (!background) setLoading(true)

      try {
        const { case: c } = await CasesService.getCase(id)
        setCase(c)
      } catch (error) {
        onError?.(error)
      } finally {
        setLoading(false)
      }
    },
    [id, onError]
  )

  // Load case details when ID changes
  useEffect(() => {
    reload()
  }, [reload])

  // No case yet, show loading
  if (loading)
    return <Loading variant="fullscreen" message="Retrieving case details..." />
  if (!_case) return <div>Case not found</div>

  const value: CaseContextProps = { case: _case, reload }
  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>
}
